import React from "react";
import { CheckoFinancesResponse, FinancialYearData } from "../types";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

interface Props {
  data: CheckoFinancesResponse;
}

const QUARTER_RANK: Record<string, number> = { q1: 1, q2: 2, q3: 3 };

/** Порядок отчётных дат: год закрывается позже своих кварталов. */
const periodRank = (y: FinancialYearData) =>
  (y.year || 0) * 10 + (QUARTER_RANK[y.periodQuarter || ""] ?? 4);

/** Подпись отчётной даты: «2025 г.» или «I кв. 2025». */
const periodTitle = (y: FinancialYearData) => y.periodLabel || `${y.year} г.`;

export default function FinancesTab({ data }: Props) {
  // Сравниваем две последние отчётные даты — годовые или квартальные.
  if (!data || !data.years || !Array.isArray(data.years) || data.years.length < 2) {
    return (
      <div className="bg-slate-50 p-8 rounded-2xl border border-slate-150 text-center">
        <p className="text-slate-550 text-sm font-semibold">Недостаточно данных об отчетности за 2 последовательных года для отображения динамики.</p>
      </div>
    );
  }

  // Сопоставимыми считаются даты одного типа: квартал сравниваем с тем же кварталом
  // прошлого года, а не с годовой отчётностью — иначе три месяца сравнивались бы с
  // двенадцатью. Нет сопоставимой пары — показываем две соседние даты как есть.
  const ordered = [...data.years].sort((a, b) => periodRank(b) - periodRank(a));
  const head = ordered[0];
  const sameKind = ordered.filter(y => (y.periodQuarter || "") === (head?.periodQuarter || ""));
  const years = sameKind.length >= 2 ? sameKind : ordered;
  if (years.length < 2) {
    return (
      <div className="bg-slate-50 p-8 rounded-2xl border border-slate-150 text-center">
        <p className="text-slate-550 text-sm font-semibold">Недостаточно данных об отчетности за 2 последовательных года для отображения динамики.</p>
      </div>
    );
  }

  const [y2025, y2024] = years;
  const [title25, title24] = [periodTitle(y2025), periodTitle(y2024)];

  const formatRub = (val: number) => {
    return (val || 0).toLocaleString("ru-RU") + " ₽";
  };

  const calculateChange = (val25: number, val24: number) => {
    const diff = val25 - val24;
    const pct = val24 !== 0 ? (diff / val24) * 100 : 0;
    return {
      diff,
      pct,
      isPositive: diff >= 0
    };
  };

  const metrics = [
    { name: "Выручка (строка 2110)", code: "2110", v25: y2025["2110"], v24: y2024["2110"], isMain: true },
    { name: "Себестоимость продаж (строка 2120)", code: "2120", v25: y2025["2120"], v24: y2024["2120"], isMain: false },
    { name: "Прибыль от продаж (строка 2200)", code: "2200", v25: y2025["2200"], v24: y2024["2200"], isMain: false },
    { name: "Прибыль до налогообложения (строка 2300)", code: "2300", v25: y2025["2300"], v24: y2024["2300"], isMain: false },
    { name: "Чистая прибыль (строка 2400)", code: "2400", v25: y2025["2400"], v24: y2024["2400"], isMain: true },
    { name: "Внеоборотные активы (строка 1100)", code: "1100", v25: y2025["1100"], v24: y2024["1100"], isMain: false },
    { name: "Оборотные активы (строка 1200)", code: "1200", v25: y2025["1200"], v24: y2024["1200"], isMain: false },
    { name: "Запасы (строка 1210)", code: "1210", v25: y2025["1210"], v24: y2024["1210"], isMain: false },
    { name: "Дебиторская задолженность (строка 1230)", code: "1230", v25: y2025["1230"], v24: y2024["1230"], isMain: false },
    { name: "Денежные средства (строка 1250)", code: "1250", v25: y2025["1250"], v24: y2024["1250"], isMain: false },
    { name: "Баланс АКТИВ (строка 1600)", code: "1600", v25: y2025["1600"], v24: y2024["1600"], isMain: true },
    { name: "Собственный капитал (строка 1300)", code: "1300", v25: y2025["1300"], v24: y2024["1300"], isMain: false },
    { name: "Долгосрочные обязательства (строка 1400)", code: "1400", v25: y2025["1400"], v24: y2024["1400"], isMain: false },
    { name: "Краткосрочные обязательства (строка 1500)", code: "1500", v25: y2025["1500"], v24: y2024["1500"], isMain: false },
    { name: "Кредиторская задолженность (строка 1520)", code: "1520", v25: y2025["1520"], v24: y2024["1520"], isMain: false },
    { name: "Баланс ПАССИВ (строка 1700)", code: "1700", v25: y2025["1700"], v24: y2024["1700"], isMain: true },
  ];

  // Столбец квартальной отчётности (если загружена) — понимаем динамику.
  const q = data.quarter;
  const fmtQ = (code: string): string => {
    const v = data.quarter?.values[code];
    return v != null ? formatRub(v) : "—";
  };

  const revStats = calculateChange(y2025["2110"], y2024["2110"]);
  const profitStats = calculateChange(y2025["2400"], y2024["2400"]);
  const assetStats = calculateChange(y2025["1600"], y2024["1600"]);

  return (
    <div className="space-y-6">
      {/* Visual Analytics Quick Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        
        {/* Card 1: Revenue Dynamics */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <span className="t-meta font-mono tracking-wider text-slate-400 font-bold uppercase">Выручка компании</span>
              <h4 className="text-2xl font-black font-display text-slate-900 mt-1">{formatRub(y2025["2110"])}</h4>
            </div>
            <div className={`p-2 rounded-xl text-xs font-bold font-mono flex items-center gap-1 shrink-0 ${
              revStats.isPositive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
            }`}>
              {revStats.isPositive ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
              {Math.abs(revStats.pct).toFixed(1)}%
            </div>
          </div>
          <div className="border-t border-slate-50 mt-5 pt-3.5 flex justify-between text-[11px] text-slate-505 font-medium">
            <span>Предыдущий год: {formatRub(y2024["2110"])}</span>
            <span className={revStats.isPositive ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>
              {revStats.isPositive ? "+" : ""}{formatRub(revStats.diff)}
            </span>
          </div>
        </div>

        {/* Card 2: Profit Dynamics */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <span className="t-meta font-mono tracking-wider text-slate-400 font-bold uppercase">Чистая прибыль</span>
              <h4 className="text-2xl font-black font-display text-slate-900 mt-1">{formatRub(y2025["2400"])}</h4>
            </div>
            <div className={`p-2 rounded-xl text-xs font-bold font-mono flex items-center gap-1 shrink-0 ${
              profitStats.isPositive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
            }`}>
              {profitStats.isPositive ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
              {Math.abs(profitStats.pct).toFixed(1)}%
            </div>
          </div>
          <div className="border-t border-slate-50 mt-5 pt-3.5 flex justify-between text-[11px] text-slate-505 font-medium">
            <span>Предыдущий год: {formatRub(y2024["2400"])}</span>
            <span className={profitStats.isPositive ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>
              {profitStats.isPositive ? "+" : ""}{formatRub(profitStats.diff)}
            </span>
          </div>
        </div>

        {/* Card 3: Asset Value */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <span className="t-meta font-mono tracking-wider text-slate-400 font-bold uppercase">Сумма активов (Баланс)</span>
              <h4 className="text-2xl font-black font-display text-slate-900 mt-1">{formatRub(y2025["1600"])}</h4>
            </div>
            <div className={`p-2 rounded-xl text-xs font-bold font-mono flex items-center gap-1 shrink-0 ${
              assetStats.isPositive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
            }`}>
              {assetStats.isPositive ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
              {Math.abs(assetStats.pct).toFixed(1)}%
            </div>
          </div>
          <div className="border-t border-slate-50 mt-5 pt-3.5 flex justify-between text-[11px] text-slate-505 font-medium">
            <span>Предыдущий год: {formatRub(y2024["1600"])}</span>
            <span className={assetStats.isPositive ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>
              {assetStats.isPositive ? "+" : ""}{formatRub(assetStats.diff)}
            </span>
          </div>
        </div>

      </div>

      {/* Spreadsheet table of РСБУ financial rows */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h4 className="font-extrabold font-display text-slate-900 text-sm">Финансовая отчётность и горизонтальный анализ</h4>
            <p className="text-xs text-slate-500 font-medium mt-0.5">Сравнение ключевых статей баланса по РСБУ за две последние отчётные даты ({title24} → {title25})</p>
            {y2025.periodQuarter && (
              <p className="t-micro text-slate-400 mt-1">
                В таблице — цифры отчётности как есть. В расчёте обороты (2110–2400) приведены
                к году: делим на число закрытых кварталов и умножаем на 4 — пороги методик
                рассчитаны на годовые значения. Баланс — остаток на дату, не приводится.
              </p>
            )}
          </div>
          <div className="text-[10px] font-extrabold font-mono text-indigo-700 bg-indigo-50 border border-indigo-100/50 px-2.5 py-1 rounded-lg uppercase tracking-wide">
            Источник: открытые федеральные ресурсы
          </div>
        </div>

        {/* Десктоп: таблица. На мобиле скрыта в пользу карточек ниже. */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100 text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">
                <th className="py-3 px-6">Статья отчётности (по РСБУ)</th>
                <th className="py-3 px-4 text-center">Код</th>
                <th className="py-3 px-5 text-right">{title25} (руб.)</th>
                <th className="py-3 px-5 text-right">{title24} (руб.)</th>
                {q && <th className="py-3 px-5 text-right text-emerald-700">{q.label} (руб.)</th>}
                <th className="py-3 px-5 text-right">Изменение (руб.)</th>
                <th className="py-3 px-5 text-center">Динамика (%)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-700 font-sans">
              {metrics.map((m, idx) => {
                const change = calculateChange(m.v25, m.v24);
                return (
                  <tr 
                    key={m.code} 
                    className={`transition-colors hover:bg-indigo-50/10 ${
                      m.isMain ? "bg-slate-50/40 font-semibold text-slate-900" : ""
                    }`}
                  >
                    <td className="py-3.5 px-6 font-medium text-slate-900">{m.name}</td>
                    <td className="py-3.5 px-4 text-center font-mono opacity-80">{m.code}</td>
                    <td className="py-3.5 px-5 text-right font-mono font-bold">{formatRub(m.v25)}</td>
                    <td className="py-3.5 px-5 text-right font-mono text-slate-500">{formatRub(m.v24)}</td>
                    {q && <td className="py-3.5 px-5 text-right font-mono font-semibold text-emerald-700">{fmtQ(m.code)}</td>}
                    <td className={`py-3.5 px-5 text-right font-mono font-semibold ${
                      change.isPositive ? "text-emerald-700" : "text-rose-700"
                    }`}>
                      {change.isPositive ? "+" : ""}{formatRub(change.diff)}
                    </td>
                    <td className="py-3.5 px-5 text-center">
                      <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-[11px] font-bold font-mono ${
                        change.isPositive 
                          ? "bg-emerald-50 text-emerald-700" 
                          : "bg-rose-50 text-rose-700"
                      }`}>
                        {change.isPositive ? "+" : ""}
                        {change.pct.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Мобила: каждая статья — карточка «лейбл → значение» вместо горизонтального скролла. */}
        <div className="md:hidden divide-y divide-slate-100">
          {metrics.map((m) => {
            const change = calculateChange(m.v25, m.v24);
            return (
              <div key={m.code} className={`p-4 space-y-2 ${m.isMain ? "bg-slate-50/50" : ""}`}>
                <div className="flex justify-between items-start gap-3">
                  <span className={`text-sm leading-snug ${m.isMain ? "font-bold text-slate-900" : "font-medium text-slate-800"}`}>
                    {m.name}
                  </span>
                  <span className="t-micro font-mono text-slate-400 shrink-0 mt-1">{m.code}</span>
                </div>
                <div className="flex justify-between items-baseline gap-2">
                  <span className="text-xs text-slate-500">{title25}</span>
                  <span className="font-mono font-bold text-sm text-slate-900">{formatRub(m.v25)}</span>
                </div>
                <div className="flex justify-between items-baseline gap-2">
                  <span className="text-xs text-slate-500">{title24}</span>
                  <span className="font-mono text-sm text-slate-500">{formatRub(m.v24)}</span>
                </div>
                {q && (
                  <div className="flex justify-between items-baseline gap-2">
                    <span className="text-xs text-emerald-700 font-semibold">{q.label}</span>
                    <span className="font-mono font-bold text-sm text-emerald-700">{fmtQ(m.code)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center gap-2 pt-2 border-t border-slate-100">
                  <span className={`font-mono font-semibold text-sm ${change.isPositive ? "text-emerald-700" : "text-rose-700"}`}>
                    {change.isPositive ? "+" : ""}{formatRub(change.diff)}
                  </span>
                  <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-bold font-mono ${
                    change.isPositive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                  }`}>
                    {change.isPositive ? "+" : ""}{change.pct.toFixed(1)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
